import { HttpClient } from '@angular/common/http';
import { inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map, timeout } from 'rxjs';
import { ConnectionStatus, Network } from '@capacitor/network';
import { Platform } from '@ionic/angular';
import { environment } from '../../../../environments/environment';
import { UserService } from '../auth/user.service';

const HEALTH_CHECK_TIMEOUT: number = 2000;

export enum NetworkStatus {
  ONLINE = 'online',
  OFFLINE = 'offline',
  DEGRADED = 'degraded',
}

/**
 * Network Provider
 *
 * Ce service permet d'accéder à la connexion de l'utilisateur
 */
@Injectable({
  providedIn: 'root',
})
export class NetworkProvider {
  private readonly platform: Platform = inject(Platform);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly userService: UserService = inject(UserService);
  private readonly ngZone: NgZone = inject(NgZone);

  /**
   * Utilisé pour l'url de config de l'API Maintenance
   *
   * @type {string}
   */
  apiCheckConnectionUrl: string;

  /**
   * Stockage de l'historique des logs de network / envoie cr / synchronisation
   */
  history = [];
  /**
   * Variable pour savoir le dernier état de connexion réel
   *
   * @type {string}
   */
  reallyConnected = true;

  networkStatusSubject: BehaviorSubject<NetworkStatus> = new BehaviorSubject<NetworkStatus>(NetworkStatus.ONLINE);
  private refreshInterval;

  constructor() {
    this.apiCheckConnectionUrl = environment.apis.API_CHECK_CONNEXION_URL;
  }

  private updateNetworkStatus(newStatus: NetworkStatus) {
    this.ngZone.run(() => {
      this.networkStatusSubject.next(newStatus);
    });
  }

  initNetworkStatus(): void {
    //TODO trouver une solution pour jeter les evenements successifs de moins de 1 minute
    Network.addListener('networkStatusChange', (status) => {
      this.onNetworkStatusChanged(status);
    });

    this.startRefreshInterval();

    this.platform.pause.subscribe(() => {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    });
    this.platform.resume.subscribe(() => {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
      this.startRefreshInterval();
      this.isReallyConnected().then();
    });
  }

  onNetworkStatusChanged(status: ConnectionStatus) {
    if (status.connected) {
      this.isReallyConnected().then((isReallyConnected) => {
        this.updateNetworkStatus(this.mapNetworkStatus(status.connected, isReallyConnected));
      });
    } else {
      this.updateNetworkStatus(this.mapNetworkStatus(status.connected, false));
    }
  }

  startRefreshInterval() {
    this.refreshInterval = setInterval(() => {
      this.isReallyConnected().then();
    }, 60000);
  }

  mapNetworkStatus(isConnected: boolean, isReallyConnected: boolean): NetworkStatus {
    if (isConnected === true) {
      return isReallyConnected ? NetworkStatus.ONLINE : NetworkStatus.DEGRADED;
    }
    return isReallyConnected ? NetworkStatus.DEGRADED : NetworkStatus.OFFLINE;
  }

  /**
   * Récupération du statut de connexion du téléphone.
   */
  isConnected(): boolean {
    return this.networkStatusSubject.value === NetworkStatus.ONLINE;
  }

  getReallyConnected() {
    return this.reallyConnected;
  }

  /**
   * Récupération du statut de connexion du téléphone
   */
  async isReallyConnected() {
    /**
     * DT-21769 : Il est possible qu'au démarrage de l'application,
     * le token d'authentification au back ne soit pas encore présent dans le cache du navigateur
     * au moment de l'appel à getApiRoot.
     * Dans ce cas, l'appel au back via getApiRoot va échouer.
     * On ne souhaite pas afficher "votre réseau n'est pas stable" dans ce cas
     */
    if (this.userService.isAuthenticated()) {
      this.reallyConnected = await this.getApiRoot();
    } else {
      console.log('---> Utilisateur pas encore authentifié au back-end. On force reallyConnected à true');
      this.reallyConnected = true;
    }

    const isCurrentlyConnected =
      this.networkStatusSubject.value === NetworkStatus.ONLINE ||
      this.networkStatusSubject.value === NetworkStatus.DEGRADED;
    const newNetworkStatus = this.mapNetworkStatus(isCurrentlyConnected, this.reallyConnected);
    if (this.networkStatusSubject.value != newNetworkStatus) {
      this.updateNetworkStatus(newNetworkStatus);
    }

    return this.reallyConnected;
  }

  /**
   * Récupération de toutes les activités
   *
   * @returns Promise qui renvoie un array d'objets activité
   */
  async getApiRoot() {
    try {
      return await firstValueFrom(
        this.http.get(this.apiCheckConnectionUrl, { responseType: 'text' }).pipe(
          timeout(HEALTH_CHECK_TIMEOUT),
          map(() => true)
        )
      );
    } catch (e) {
      return false;
    }
  }

  /**
   * Ajout d'un log dans l'historique
   */
  addHistory(text = '', data = null) {
    this.history.unshift({
      date: new Date().toISOString(),
      isConnected: this.isConnected(),
      isReallyConnected: this.getReallyConnected(),
      text: text,
      data: data,
    });
  }

  /**
   * Récupération de l'historique
   */
  getHistory() {
    this.history = this.history.sort((a, b) => {
      if (new Date(a.date).getTime() == new Date(b.date).getTime()) return 0;
      return new Date(a.date).getTime() < new Date(b.date).getTime() ? 1 : -1;
    });
    return this.history;
  }

  /**
   * Suppression de l'historique
   */
  resetHistory() {
    this.history = [];
  }
}
